import { DEFAULT_PAGE_SIZE } from '@/constants';
import { usePaginatedCrudModel } from '@/models/shared/usePaginatedCrudModel';
import {
  createStore,
  deleteStore,
  downloadStores,
  fetchStores,
  reactivateStore,
  updateStore,
  uploadStoresFile,
} from '@/services/store/storeService';
import type {
  Store,
  StoreListParams,
  StoreListResult,
  StorePayload,
} from '@/types/store';
import { useCallback } from 'react';

const useStoreModel = () => {
  const {
    items: stores,
    loading,
    pagination,
    loadItems: loadStores,
    create,
    update,
    remove,
  } = usePaginatedCrudModel<
    Store,
    StoreListParams,
    StorePayload,
    StorePayload,
    StoreListResult
  >({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    initialQuery: {},
    loadList: fetchStores,
    buildQuery: (params, lastQuery, paginationState) => ({
      page: params.page ?? paginationState.current,
      size:
        params.size ??
        params.page_size ??
        params.pageSize ??
        paginationState.pageSize,
      name: 'name' in params ? params.name : lastQuery.name,
      status: 'status' in params ? params.status : lastQuery.status,
    }),
    createItem: createStore,
    updateItem: updateStore,
    deleteItem: deleteStore,
  });

  const exportStores = useCallback(async () => {
    return downloadStores();
  }, []);

  const upload = useCallback(async (file: File) => {
    return uploadStoresFile(file);
  }, []);

  const reactivate = useCallback(async (id: number) => {
    return reactivateStore(id);
  }, []);

  return {
    stores,
    loading,
    pagination,
    loadStores,
    create,
    update,
    remove,
    reactivate,
    exportStores,
    upload,
  };
};

export default useStoreModel;
